'use client';

import { type CSSProperties, type MouseEvent } from 'react';
import { SIcon, type SIconName } from '../SIcon';
import { SButton, type SButtonColor } from '../SButton';
import { SGhostButton } from '../SGhostButton';

export type SToastType = 'default' | 'danger' | 'caution' | 'complete' | 'accent' | 'info';

const BUTTON_STYLE: Record<SToastType, { color: SButtonColor; outline?: boolean }> = {
  default: { color: 'secondary' },
  danger: { color: 'danger', outline: true },
  caution: { color: 'neutral', outline: true },
  complete: { color: 'neutral', outline: true },
  accent: { color: 'neutral', outline: true },
  info: { color: 'secondary' },
};

export interface SToastProps {
  /** 좌측 아이콘 */
  icon?: SIconName;
  /** 메시지 */
  message: string;
  /** 우측 링크 URL */
  link?: string;
  /** 링크 레이블 (없으면 URL) */
  linkLabel?: string;
  /** 우측 버튼 레이블 */
  buttonLabel?: string;
  /** 닫기 버튼 표시 */
  useClose?: boolean;
  /** 타입(색상/프리셋) */
  type?: SToastType;
  /** 닫기 (sdClose) */
  onClose?: () => void;
  /** 버튼 클릭 (sdButtonClick) */
  onButtonClick?: (e: MouseEvent) => void;
  className?: string;
  style?: CSSProperties;
}

/** SToast — sd-toast 포팅. 타입별 색상 + 아이콘/링크/버튼/닫기. */
export function SToast({
  icon,
  message,
  link,
  linkLabel,
  buttonLabel,
  useClose = false,
  type = 'default',
  onClose,
  onButtonClick,
  className,
  style,
}: SToastProps) {
  const bg = `var(--cmp-toast-${type}-bg)`;
  const content =
    type === 'info' ? 'var(--cmp-toast-info-text)' : `var(--cmp-toast-${type}-content)`;
  const iconColor = type === 'info' ? 'var(--cmp-toast-info-icon)' : content;
  const linkColor = type === 'info' ? 'var(--cmp-toast-info-textLink)' : content;
  const defaultCloseIntent = ['default', 'caution', 'info'].includes(type);

  return (
    <div
      role="status"
      aria-live="polite"
      aria-atomic="true"
      className={className}
      style={{
        display: 'inline-flex',
        alignItems: 'center',
        gap: 'var(--cmp-toast-gap)',
        padding: 'var(--cmp-toast-paddingY) var(--cmp-toast-paddingX)',
        borderRadius: 'var(--cmp-toast-radius)',
        background: bg,
        color: content,
        boxShadow: '2px 2px 8px 2px #00000033',
        ...style,
      }}
    >
      {icon && <SIcon name={icon} size={16} color={iconColor} />}
      <span
        className="text-[14px] font-medium leading-[24px] text-nowrap"
        style={{ color: content }}
      >
        {message}
      </span>
      {link && (
        <a
          href={link}
          target="_blank"
          rel="noopener noreferrer"
          className="text-[14px] font-bold leading-[24px] underline text-nowrap"
          style={{ color: linkColor }}
        >
          {linkLabel || link}
        </a>
      )}
      {buttonLabel && (
        <SButton
          {...BUTTON_STYLE[type]}
          size="sm"
          label={buttonLabel}
          onClick={e => onButtonClick?.(e)}
        />
      )}
      {useClose && (
        <SGhostButton
          icon="close"
          size="xs"
          intent={defaultCloseIntent ? 'default' : 'inverse'}
          ariaLabel="close"
          onClick={() => onClose?.()}
        />
      )}
    </div>
  );
}
